import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { assertNoEmojiInNumber, formatCount, formatDelta, type DeltaLabels } from '../lib/format'

/* ─────────────────────────────────────────────────────────── Button ── */

/**
 * DashboardShell button. 38–42px, `rounded-xl`. design-system.md §8.
 *
 * `--color-danger` IS permitted here — danger is DashboardShell-only, and this
 * is DashboardShell. It stays forbidden in KidShell, where wrong is coral.
 */
export type DashButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'neutral'

/**
 * 🔴 44px UNDER A THUMB, 32px UNDER A MOUSE — ONE HEIGHT PER POINTER, NOT ONE
 * PER PRODUCT.
 *
 * Every variant was a flat `h-8`. 32px is a fine button under a mouse and it is
 * under the platform's 44px minimum under a thumb, which left "Give feedback"
 * and "Print or save as PDF" — the parent tier's only actions on those two
 * screens — smaller than a tap reliably lands on. The docblock above has always
 * claimed *"38–42px"*; `h-8` never was.
 *
 * 🔴 `pointer-fine:` RATHER THAN `sm:`, AND THE LANDSCAPE PHONE IS WHY. A width
 * breakpoint says "wide, therefore mouse", and `responsive.spec.ts` already
 * keeps a viewport that disproves it: 844×390 is a phone turned sideways. Under
 * `sm:` every control on it would collapse back to its mouse size while still
 * being driven by a thumb. The media query asks what is actually true —
 * `pointer: coarse` is the finger, `fine` is the mouse — so the size follows the
 * hand rather than the window. A touchscreen laptop gets the larger target too,
 * which is the correct answer there as well.
 *
 * ⚠️ THE SPLIT IS WHAT MAKES THIS SAFE IN A SHARED PRIMITIVE. `DashButton` draws
 * buttons in 28 files across all four tiers, and the school, teacher and operator
 * consoles are read on laptops — on every one of those this renders exactly as it
 * did.
 */
const VARIANT: Record<DashButtonVariant, { h: string; style: React.CSSProperties }> = {
  primary: {
    h: 'h-[44px] pointer-fine:h-8',
    style: { background: 'var(--color-primary)', color: 'var(--color-primary-ink)' },
  },
  secondary: {
    h: 'h-[44px] pointer-fine:h-8',
    style: { background: 'var(--color-surface)', border: '1px solid var(--color-line-strong)' },
  },
  ghost: { h: 'h-[44px] pointer-fine:h-8', style: { background: 'transparent' } },
  destructive: {
    h: 'h-[44px] pointer-fine:h-8',
    style: {
      background: 'var(--color-surface)',
      border: '1px solid var(--color-pill-danger-line)',
      color: 'var(--color-danger)',
    },
  },
  neutral: {
    h: 'h-[44px] pointer-fine:h-8',
    style: { background: 'var(--color-surface)', border: '1px solid var(--color-line)' },
  },
}

export interface DashButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: DashButtonVariant
}

export function DashButton({ variant = 'primary', children, ...rest }: DashButtonProps) {
  const v = VARIANT[variant]
  return (
    <button
      type="button"
      className={`inline-flex ${v.h} items-center justify-center gap-1.5 rounded-md px-3.5 font-dash text-dash-sm font-bold text-ink shadow-[0_1px_1px_rgba(0,0,0,0.06)] disabled:opacity-50`}
      style={v.style}
      {...rest}
    >
      {children}
    </button>
  )
}

/* ──────────────────────────────────────────────────────── StatTile ── */

export interface StatTileProps {
  label: ReactNode
  /**
   * 🔴 A NUMBER, never a pre-formatted string. If this took a string the
   * formatter would be optional, and "58.99999999999997%" is exactly the bug
   * that reached a paying parent (BUILD-BRIEF §7).
   */
  value: number
  /** Week-over-week change. Rendered as "+3 vs last week" / "same as last week". */
  delta?: number
  /** 🔴 Required alongside `delta` — the formatter does the number, not the words. */
  deltaLabels?: DeltaLabels
  /** Used instead of `delta` for a non-comparative caption, e.g. "days in a row". */
  caption?: ReactNode
  /** 🔴 Sits BESIDE the numeral, never inside it. A streak is `4` and a flame, not `4 🔥`. */
  icon?: ReactNode
}

export function StatTile({ label, value, delta, deltaLabels, caption, icon }: StatTileProps) {
  const numeral = assertNoEmojiInNumber(formatCount(value))

  return (
    <div
      className="flex min-w-40 flex-col gap-1 rounded-lg bg-surface p-4 font-dash"
      style={{ border: '1px solid var(--color-line)' }}
    >
      <span className="text-dash-xs text-muted">{label}</span>
      <span className="flex items-center gap-2">
        <span className="text-dash-2xl font-extrabold tabular-nums text-ink">{numeral}</span>
        {icon ? (
          <span aria-hidden="true" className="inline-flex shrink-0">
            {icon}
          </span>
        ) : null}
      </span>
      <span className="text-dash-xs text-muted">
        {delta !== undefined && deltaLabels ? formatDelta(delta, deltaLabels) : caption}
      </span>
    </div>
  )
}

/* ──────────────────────────────────────────────────── RosterTable ── */

export interface RosterRow {
  rollNumber: string
  child: string
  /** 🔴 Shown so the wrong child never gets the session. Null when unlinked. */
  parent: string | null
  minutes: number
  status: ReactNode
}

export interface RosterTableProps {
  rows: readonly RosterRow[]
  selectedRoll?: string
  onSelect?: (rollNumber: string) => void
  headers: { roll: string; child: string; parent: string; minutes: string; status: string }
  /** Shown in place of a parent name when the row has none. */
  unlinkedLabel: string
}

/**
 * Dense, sortable, ZEBRA-FREE. Selected row uses `primary-tint`.
 *
 * 🔴 The linked parent column is not decoration — it is the roster import's
 * output on screen, and it is what stops a whole session landing on the wrong
 * child (database.md §4, wireframes D2).
 */
export function RosterTable({
  rows,
  selectedRoll,
  onSelect,
  headers,
  unlinkedLabel,
}: RosterTableProps) {
  return (
    /*
     * 🔴 A ROSTER IS FIVE COLUMNS AND A PHONE IS 328px OF CONTENT. Roll, child,
     * parent, minutes and status do not fit, and this component draws its own
     * table rather than going through `TableCard`, so it never got that
     * component's scroll container. Sideways, not restacked — the reasoning is
     * in `TableCard`'s docblock and it is the same reasoning here: an admin
     * scans the *status* column down thirty children looking for the unlinked
     * one, and a stack of thirty cards has no column to scan.
     *
     * ⚠️ `tabIndex={0}` MAKES THE SCROLL REGION REACHABLE. A `div` that scrolls
     * is not focusable, and these rows contain no controls, so without it a
     * keyboard reader has no way to move the table sideways at all.
     */
    <div className="scroll-x" tabIndex={0}>
    <table className="w-full min-w-[34rem] border-collapse font-dash text-dash-sm">
      <thead>
        <tr className="text-left text-dash-xs text-muted">
          {[headers.roll, headers.child, headers.parent, headers.minutes, headers.status].map(
            (h) => (
              <th key={String(h)} className="border-b border-line px-3 py-2 font-bold">
                {h}
              </th>
            ),
          )}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const selected = r.rollNumber === selectedRoll
          return (
            <tr
              key={r.rollNumber}
              onClick={() => onSelect?.(r.rollNumber)}
              className="cursor-pointer"
              style={{ background: selected ? 'var(--color-primary-tint)' : undefined }}
            >
              <td className="border-b border-line px-3 py-2 font-bold tabular-nums text-ink">
                {r.rollNumber}
              </td>
              <td className="border-b border-line px-3 py-2 text-ink">{r.child}</td>
              <td className="border-b border-line px-3 py-2 text-ink-soft">
                {r.parent ?? <span className="text-muted">{unlinkedLabel}</span>}
              </td>
              <td className="border-b border-line px-3 py-2 tabular-nums text-ink">
                {formatCount(r.minutes)}
              </td>
              <td className="border-b border-line px-3 py-2">{r.status}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
    </div>
  )
}
